import {
    DialogContent,
    Button,
    Stack,
    TextField,
    useTheme,
    Typography,
    Paper,
    Box,
    MenuItem,
    Select,
    InputLabel,
    Rating,
    Chip,
    Collapse,
    FormControl,
    Tab,
    Tabs,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import AssistantIcon from '@mui/icons-material/Assistant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PositionAutocompleteControl from "../components/PositionAutocompleteControl";
import { useErrorDisplay } from "../components/ErrorDisplay";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { IsEmpty } from "../Helpers";
import { I18N } from "../I18N";
import { ProcessType } from "../models/dtos";
import PositionClient, { PositionErrors } from "../apiClients/PositionClient";
import { TemplateErrors } from "../apiClients/TemplateClient";
import AiTemplateClient, { AiChatMessage, AiContextualAiAnswer, AiContextualCoverageDecisionWire, AiFeedbackOutcome, AiTemplateCoverageConfirmation, AiTemplateItem, AiTemplatePreview, AiTemplateSuggestionDecision } from "../apiClients/AiTemplateClient";
import { useSnackBarDisplay } from "../components/SnackBarDisplay";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import ContextualAiDialog from "../components/ai/ContextualAiDialog";
import ContextualAiWorkspace from "../components/ai/ContextualAiWorkspace";
import { ContextualAiWorkflowAdapter } from "../components/ai/contextualAiTypes";
import { buildTemplateDiscoveryInterview } from "../components/ai/templateDiscovery";
import { recallContextSummary, rememberContextSummary } from "../components/ai/ContextualAiContextMemory";
import { buildContextualRequest } from "../components/ai/contextualAiWire";

export interface TemplateAiUploadPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const TemplateAiUploadPopup = ({ onAdd = async () => { }, icon = <AssistantIcon />, ...btnProps }: TemplateAiUploadPopupProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [SnackBarDisplay, showSnackBar] = useSnackBarDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();
    const singlePane = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    const [positionId, setPositionId] = useState<string>('');
    const [positionTitle, setPositionTitle] = useState<string>('');
    const [templateName, setTemplateName] = useState<string>('');
    const [processType, setProcessType] = useState<ProcessType>(ProcessType.Evaluate);
    const [jobDescription, setJobDescription] = useState<string>('');
    const [showJobDescription, setShowJobDescription] = useState<boolean>(false);

    const [templateMessages, setTemplateMessages] = useState<AiChatMessage[]>([]);
    const [templateInteractionId, setTemplateInteractionId] = useState<string>('');
    const [aiRating, setAiRating] = useState<number | null>(null);
    const [aiFeedback, setAiFeedback] = useState<string>('');

    const [preview, setPreview] = useState<AiTemplatePreview | null>(null);
    const [previousPreview, setPreviousPreview] = useState<AiTemplatePreview | null>(null);
    const [aiBusy, setAiBusy] = useState<boolean>(false);
    const [suggestionDecisions, setSuggestionDecisions] = useState<Record<string, AiTemplateSuggestionDecision>>({});
    const [discoveryIndex, setDiscoveryIndex] = useState(0);
    const [coverageConfirmations, setCoverageConfirmations] = useState<AiTemplateCoverageConfirmation[]>([]);
    const [serverCoverageDecisions, setServerCoverageDecisions] = useState<AiContextualCoverageDecisionWire[]>([]);
    const serverCoverageDecisionsRef = useRef<AiContextualCoverageDecisionWire[]>([]);
    const [changedCoverageGroups, setChangedCoverageGroups] = useState<string[]>([]);
    const [contextAnswers, setContextAnswers] = useState<AiContextualAiAnswer[]>([]);
    const [roleContextSummary, setRoleContextSummary] = useState('');
    const [workspaceSection, setWorkspaceSection] = useState<'details' | 'preview'>('details');
    const [aiDialogOpen, setAiDialogOpen] = useState<boolean>(false);
    const templateWorkspaceRef = useRef<HTMLDivElement | null>(null);
    const previewRequestInFlightRef = useRef(false);
    const recalledPositionContext = roleContextSummary || recallContextSummary(`position:${positionId}`);
    const discoveryQuestions = useMemo(() => buildTemplateDiscoveryInterview({ jobDescription, knownContext: recalledPositionContext }), [jobDescription, recalledPositionContext]);

    const disableGenerate = IsEmpty(positionId) || IsEmpty(templateName) || IsEmpty(jobDescription);
    const includedPreview = preview ? buildIncludedPreview(preview, suggestionDecisions) : null;
    const hasConfirmedCoverage = serverCoverageDecisions.length > 0 && serverCoverageDecisions.every(decision => coverageConfirmations.some(confirmation => confirmation.ProficiencyGroup === decision.ProficiencyGroup && confirmation.CoverageFingerprint === decision.CoverageFingerprint));
    const disableCreate = !preview || !hasConfirmedCoverage;

    const title = I18N.Translate('Generate Template');

    const handleOpen = async () => {
        setWorkspaceSection('details');
        setAiDialogOpen(false);
        setOpen(true);
    };

    const handleClose = () => {
        setAiDialogOpen(false);
        setOpen(false);
        setPreview(null);
        setPreviousPreview(null);
        setTemplateMessages([]);
        setTemplateInteractionId('');
        setAiRating(null);
        setAiFeedback('');
        setPositionId('');
        setPositionTitle('');
        setTemplateName('');
        setJobDescription('');
        setShowJobDescription(false);
        setProcessType(ProcessType.Evaluate);
        setSuggestionDecisions({});
        setDiscoveryIndex(0);
        setCoverageConfirmations([]);
        setServerCoverageDecisions([]);
        serverCoverageDecisionsRef.current = [];
        setChangedCoverageGroups([]);
        setContextAnswers([]);
        setRoleContextSummary('');
        setWorkspaceSection('details');
    };

    const handlePositionSelect = async (selectedId: string) => {
        try {
            setBusy(true);
            setPositionId(selectedId);

            const position = await new PositionClient().GetSingle(selectedId);
            setPositionTitle(position.Title);
            setJobDescription(position.Description ?? '');
            if (IsEmpty(templateName)) {
                setTemplateName(`${position.Title} Template`);
            }
            setPreview(null);
            setPreviousPreview(null);
            setTemplateMessages([]);
            setTemplateInteractionId('');
            setAiRating(null);
            setAiFeedback('');
            setSuggestionDecisions({});
            setDiscoveryIndex(0);
            setCoverageConfirmations([]);
            setServerCoverageDecisions([]);
            serverCoverageDecisionsRef.current = [];
            setChangedCoverageGroups([]);
            setContextAnswers([]);
            setRoleContextSummary(recallContextSummary(`position:${selectedId}`));
            setWorkspaceSection('details');
        } catch (error) {
            setError(error, PositionErrors.GetSingle);
        } finally {
            setBusy(false);
        }
    };

    const collectServerCoverage = async (
        effectivePreview: AiTemplatePreview,
        decisions: Record<string, AiTemplateSuggestionDecision>,
        confirmations: AiTemplateCoverageConfirmation[],
        changedGroups: string[] = [],
        answers: AiContextualAiAnswer[] = contextAnswers,
    ) => {
        const client = new AiTemplateClient();
        const discovered: AiContextualCoverageDecisionWire[] = [];
        let probeConfirmations = [...confirmations];
        let changed = changedGroups;
        let coverageContractVersion = coverageVersionFor(serverCoverageDecisionsRef.current);
        for (let index = 0; index < 5; index += 1) {
            const response = await client.ContinueContextualChat(buildContextualRequest('template-generation', 'coverage-regenerate', {
                PositionId: positionId,
                PositionTitle: positionTitle || templateName,
                JobDescription: jobDescription,
                Answers: answers,
                Messages: templateMessages.map(({ role, content }) => ({ role, content })),
                ContextSummary: roleContextSummary || recalledPositionContext,
                GenerateNow: false,
                InteractionId: templateInteractionId || undefined,
                TemplateName: templateName,
                ProcessType: processType,
                ExistingPreview: effectivePreview,
                Regenerate: false,
                SuggestionDecisions: buildSuggestionDecisionPayload(decisions),
                CoverageConfirmations: probeConfirmations,
                CoverageContractVersion: coverageContractVersion,
                ChangedCoverageGroups: changed,
            }));
            changed = [];
            if (!response.CoverageDecision)
                break;
            const decision = response.CoverageDecision;
            coverageContractVersion = decision.CoverageContractVersion;
            if (discovered.some(item => item.ProficiencyGroup === decision.ProficiencyGroup && item.CoverageFingerprint === decision.CoverageFingerprint))
                break;
            discovered.push(decision);
            probeConfirmations = [
                ...probeConfirmations.filter(item => item.ProficiencyGroup !== decision.ProficiencyGroup),
                { ProficiencyGroup: decision.ProficiencyGroup, CoverageFingerprint: decision.CoverageFingerprint, ConfirmedSufficient: true, ContextSummary: response.ContextSummary || roleContextSummary || 'Coverage reviewed in Synergist AI.' },
            ];
        }
        serverCoverageDecisionsRef.current = discovered;
        setServerCoverageDecisions(discovered);
        return discovered;
    };

    const handleGeneratePreview = async (regenerate: boolean, messagesOverride?: AiChatMessage[], answersOverride?: AiContextualAiAnswer[]): Promise<AiTemplatePreview | null> => {
        // The preview action can otherwise be entered twice before React commits
        // the busy state (for example, a fast double click). Keep the conversation
        // and preview bound to one response at a time.
        if (previewRequestInFlightRef.current)
            return null;

        previewRequestInFlightRef.current = true;
        try {
            setAiBusy(true);
            const messages = messagesOverride ?? templateMessages;
            const response = await new AiTemplateClient().GenerateTemplatePreview({
                PositionId: positionId,
                PositionTitle: positionTitle || templateName,
                TemplateName: templateName,
                ProcessType: processType,
                JobDescription: jobDescription,
                Messages: messages.map(({ role, content }) => ({ role, content })),
                ExistingPreview: regenerate ? null : preview,
                Regenerate: regenerate,
                InteractionId: templateInteractionId || undefined,
                RoleContextSummary: roleContextSummary || recalledPositionContext,
            });

            setPreviousPreview(preview);
            setPreview(response);
            setTemplateInteractionId(response.InteractionId);
            if (!IsEmpty(response.AssistantMessage)) {
                setTemplateMessages(appendAssistantResponse(messages, response.AssistantMessage!));
            }
            setCoverageConfirmations([]);
            setChangedCoverageGroups([]);
            await collectServerCoverage(response, suggestionDecisions, [], [], answersOverride ?? contextAnswers);
            if (singlePane && messagesOverride === undefined)
                setWorkspaceSection('preview');
            return response;
        } catch (error) {
            setError(error, TemplateErrors.Generate);
            return null;
        } finally {
            previewRequestInFlightRef.current = false;
            setAiBusy(false);
        }
    };

    const handleSuggestionDenialToggle = (groupName: string, item: AiTemplateItem, deny: boolean) => {
        const key = getSuggestionKey(groupName, item);
        const next = { ...suggestionDecisions };
        if (deny) {
            next[key] = {
                ProficiencyGroup: groupName,
                Description: item.Description,
                Type: item.Type,
                IsNegative: item.IsNegative,
                Decision: 'denied'
            };
        } else {
            delete next[key];
        }
        setSuggestionDecisions(next);
        const retainedConfirmations = coverageConfirmations.filter(item => item.ProficiencyGroup !== groupName);
        setCoverageConfirmations(retainedConfirmations);
        setChangedCoverageGroups([groupName]);
        if (preview)
            void collectServerCoverage(buildIncludedPreview(preview, next), next, retainedConfirmations, [groupName]);
        setTemplateMessages(current => [
            ...current,
            deny
                ? {
                    role: 'user',
                    content: `I denied the ${item.Type.toLowerCase()} "${item.Description}" in ${groupName}. Do not include it or a substantially similar proficiency in this template.`
                }
                : {
                    role: 'user',
                    content: `I restored the ${item.Type.toLowerCase()} "${item.Description}" in ${groupName}. Include it in this template again.`
                }
        ]);
    };

    const handleApprove = async () => {
        if (!preview)
            return;

        try {
            setAiBusy(true);
            const response = await new AiTemplateClient().ApproveTemplate({
                PositionId: positionId,
                TemplateName: templateName,
                ProcessType: processType,
                JobDescription: jobDescription,
                Preview: includedPreview!,
                InteractionId: templateInteractionId,
                Rating: aiRating,
                Feedback: aiFeedback,
                SuggestionDecisions: buildSuggestionDecisionPayload(suggestionDecisions),
                CoverageConfirmations: coverageConfirmations,
                CoverageContractVersion: coverageVersionFor(serverCoverageDecisions),
                ChangedCoverageGroups: changedCoverageGroups,
            });
            if (response.IsError) {
                showSnackBar(`Failed to create template: ${response.Errors?.[0] ?? 'Unknown error'}`, true);
                return;
            }

            await onAdd(response.Content);
            showSnackBar('Template created successfully!');
            handleClose();
        } catch (error) {
            setError(error, TemplateErrors.Create);
            showSnackBar('Failed to create template.', true);
        } finally {
            setAiBusy(false);
        }
    };

    const recordTemplateFeedback = async (outcome: AiFeedbackOutcome) => {
        if (IsEmpty(templateInteractionId))
            return;

        await new AiTemplateClient().RecordFeedback({
            InteractionId: templateInteractionId,
            PositionId: positionId || undefined,
            Workflow: 'TemplatePreview',
            Outcome: outcome,
            Rating: aiRating,
            Feedback: aiFeedback,
            FinalPreview: preview ?? undefined,
            SuggestionDecisions: preview ? buildSuggestionDecisionPayload(suggestionDecisions) : undefined
        });
    };

    const handleCancel = async () => {
        try {
            setAiBusy(true);
            await recordTemplateFeedback('Dismissed');
        } catch (error) {
            console.warn('Unable to record dismissed AI interaction.', error);
        } finally {
            setAiBusy(false);
            handleClose();
        }
    };

    const handleReject = async () => {
        try {
            setAiBusy(true);
            await recordTemplateFeedback('Rejected');
            showSnackBar('AI feedback recorded.');
            handleClose();
        } catch (error) {
            setError(error, PositionErrors.Update);
        } finally {
            setAiBusy(false);
        }
    };

    const previewDiff = computePreviewDiff(previousPreview, preview);
    const renderTemplateConversation = () => {
        const currentQuestion = !preview ? discoveryQuestions[discoveryIndex] : undefined;
        const adapter: ContextualAiWorkflowAdapter<AiTemplatePreview> = {
            workflow: 'template-generation',
            sessionKey: `template:${positionId}:${processType}`,
            voiceWorkflow: 'TemplateCreation',
            contextId: positionId || null,
            capabilities: { choices: true, freeText: true, skip: !preview, generateNow: !preview, editableContextSummary: true, voice: true },
            initialMessages: templateMessages.filter(message => message.role !== 'system').map(message => ({ role: message.role as 'user' | 'assistant', content: message.content })),
            initialContextSummary: recalledPositionContext,
            initialQuestion: currentQuestion?.prompt,
            initialChoices: currentQuestion?.choices,
            initialCoverageDecision: findServerCoverageDecision(serverCoverageDecisions, coverageConfirmations, changedCoverageGroups),
            initialCoverageSummary: serverCoverageDecisions.length > 0
                && !hasConfirmedCoverage
                && !findServerCoverageDecision(serverCoverageDecisions, coverageConfirmations, changedCoverageGroups)
                ? buildServerCoverageSummary(serverCoverageDecisions)
                : null,
            progressLabel: preview ? 'Refining template' : `Discovery ${Math.min(discoveryIndex + 1, discoveryQuestions.length)} of ${discoveryQuestions.length}`,
            submit: async request => {
                const answeredGap = !preview && request.action === 'answer' && currentQuestion && request.text?.trim()
                    ? discoveryTopicGap[currentQuestion.topic]
                    : undefined;
                const nextContextAnswers = answeredGap
                    ? [...contextAnswers.filter(item => item.GapKey !== answeredGap), { GapKey: answeredGap, Answer: request.text!.trim() }]
                    : contextAnswers;
                if (answeredGap)
                    setContextAnswers(nextContextAnswers);
                if (!preview && (request.action === 'answer' || request.action === 'skip') && discoveryIndex < discoveryQuestions.length - 1) {
                    const nextIndex = discoveryIndex + 1;
                    setDiscoveryIndex(nextIndex);
                    const next = discoveryQuestions[nextIndex];
                    return {
                        assistantMessage: request.action === 'skip' ? 'Skipped. We can return to that later.' : 'Got it. I will use that when shaping the role criteria.',
                        question: next.prompt,
                        choices: next.choices,
                        canSkip: true,
                        canGenerateNow: true,
                        progressLabel: `Discovery ${nextIndex + 1} of ${discoveryQuestions.length}`,
                    };
                }

                if (request.action === 'coverage-confirm-sufficient') {
                    const category = String(request.metadata?.category ?? '');
                    const fingerprint = String(request.metadata?.coverageFingerprint ?? '');
                    const serverDecision = serverCoverageDecisions.find(item => item.ProficiencyGroup === category && item.CoverageFingerprint === fingerprint);
                    const requestedVersion = Number(request.metadata?.coverageContractVersion);
                    if (!serverDecision || requestedVersion !== serverDecision.CoverageContractVersion)
                        throw new Error('Coverage changed. Please review the latest Synergist AI coverage summary.');
                    const contextSummary = request.contextSummary?.trim() || roleContextSummary.trim() || recalledPositionContext.trim() || 'Coverage reviewed with Synergist AI.';
                    const nextConfirmations = [
                        ...coverageConfirmations.filter(item => item.ProficiencyGroup !== category),
                        { ProficiencyGroup: category, CoverageFingerprint: serverDecision.CoverageFingerprint, ConfirmedSufficient: true, ContextSummary: contextSummary },
                    ];
                    setCoverageConfirmations(nextConfirmations);
                    const nextChanged = changedCoverageGroups.filter(item => item !== category);
                    setChangedCoverageGroups(nextChanged);
                    const nextDecision = findServerCoverageDecision(serverCoverageDecisions, nextConfirmations, nextChanged);
                    return {
                        assistantMessage: `${category} is confirmed sufficient for this role.`,
                        needsCoverageDecision: nextDecision,
                        coverageSummary: !nextDecision && !serverCoverageIsConfirmed(serverCoverageDecisions, nextConfirmations)
                            ? buildServerCoverageSummary(serverCoverageDecisions)
                            : null,
                    };
                }

                if (request.action === 'coverage-confirm-all-sufficient') {
                    const confirmations = Array.isArray(request.metadata?.confirmations)
                        ? request.metadata!.confirmations as Array<{ category?: string; coverageFingerprint?: string; coverageContractVersion?: number }>
                        : [];
                    const contextSummary = request.contextSummary?.trim() || roleContextSummary.trim() || recalledPositionContext.trim() || 'Template coverage reviewed with Synergist AI.';
                    const requested = new Map(confirmations
                        .filter(item => item.category && item.coverageFingerprint && item.coverageContractVersion)
                        .map(item => [item.category!, item.coverageFingerprint!]));
                    const requestedVersion = Number(request.metadata?.coverageContractVersion);
                    const next = serverCoverageDecisions
                        .filter(item => requested.get(item.ProficiencyGroup) === item.CoverageFingerprint && item.CoverageContractVersion === requestedVersion)
                        .map(item => ({
                            ProficiencyGroup: item.ProficiencyGroup,
                            CoverageFingerprint: item.CoverageFingerprint,
                            ConfirmedSufficient: true,
                            ContextSummary: contextSummary,
                        }));
                    if (next.length !== serverCoverageDecisions.length)
                        throw new Error('Coverage changed. Please review the latest Synergist AI coverage summary.');
                    setCoverageConfirmations(next);
                    setChangedCoverageGroups([]);
                    return {
                        assistantMessage: 'Coverage is confirmed across the role. The included criteria are ready to save.',
                        needsCoverageDecision: null,
                        coverageSummary: null,
                    };
                }

                const coverageInstruction = request.action === 'coverage-add-suggestions'
                    ? `Add the role-specific suggested criteria for ${String(request.metadata?.category ?? 'this category')}.`
                    : request.action === 'coverage-regenerate'
                        ? `Explore whether ${String(request.metadata?.category ?? 'this category')} has any meaningful gaps without padding or duplicating criteria.`
                        : request.action === 'coverage-describe-missing'
                            ? request.text
                            : undefined;
                const nextMessages = coverageInstruction && request.action !== 'answer'
                    ? [...request.messages, { role: 'user' as const, content: coverageInstruction }]
                    : request.messages;
                const response = await handleGeneratePreview(Boolean(request.action === 'coverage-regenerate'), nextMessages, nextContextAnswers);
                if (!response)
                    throw new Error('Template generation failed');
                setWorkspaceSection('preview');
                setCoverageConfirmations([]);
                const decisions = serverCoverageDecisionsRef.current;
                const nextCoverageDecision = findServerCoverageDecision(decisions, [], []);
                return {
                    assistantMessage: response.AssistantMessage,
                    result: response,
                    readyToGenerate: true,
                    canSkip: false,
                    canGenerateNow: false,
                    progressLabel: 'Template ready for review',
                    needsCoverageDecision: nextCoverageDecision,
                    coverageSummary: nextCoverageDecision ? null : buildServerCoverageSummary(decisions),
                };
            },
        };
        return (
            <ContextualAiWorkspace
                adapter={adapter}
                testId="template-ai-workspace"
                transcriptTestId="template-chat-scroll"
                composerTestId="template-ai-composer"
                composerLabel={preview ? 'Describe the changes you want' : 'Answer in your own words'}
                emptyMessage="A short role conversation helps Synergist AI identify the abilities, positive practices, and meaningful risks that the job description does not explain."
                onMessagesChange={messages => setTemplateMessages(messages)}
                onContextSummaryChange={summary => {
                    setRoleContextSummary(summary);
                    rememberContextSummary(`position:${positionId}`, summary);
                }}
                footer={preview ? (
                    <Stack spacing={1} sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center"><Typography variant="caption">Rate this suggestion</Typography><Rating size="small" value={aiRating} onChange={(_, value) => setAiRating(value)} /></Stack>
                        <TextField fullWidth size="small" label="What was useful or needs correction?" value={aiFeedback} onChange={event => setAiFeedback(event.target.value)} inputProps={{ maxLength: 2000 }} />
                    </Stack>
                ) : undefined}
            />
        );
    };

    return (
        <>
            <ErrorDisplay />
            <SnackBarDisplay />
            <IconButtonEx disabled={busy} hoverContent={title} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

            <ResponsiveDialog
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                maxWidth="xl"
                widthTier="wide"
                sizingStrategy="workspace"
                tabletFullScreen="portrait"
                PaperProps={{
                    sx: {
                        height: 'min(900px, calc(100dvh - 48px))',
                        maxHeight: 'calc(100dvh - 48px)',
                        overflow: 'hidden'
                    }
                }}
            >
                <BusySpinner busy={busy || (aiBusy && !aiDialogOpen)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <ResponsiveDialogHeader id="customized-dialog-title" title={title} />
                        <DialogContent
                            dividers
                            data-testid="template-workspace"
                            sx={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                padding: viewport.isPhone ? theme.spacing(1) : theme.spacing(2),
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {singlePane && (
                                <Tabs
                                    value={workspaceSection}
                                    onChange={(_, value) => setWorkspaceSection(value)}
                                    variant="fullWidth"
                                    aria-label="Template generator workspace"
                                    sx={{ flex: '0 0 auto', mb: 1 }}
                                >
                                    <Tab id="template-workspace-tab-details" aria-controls="template-workspace-panel-details" value="details" label="Details" />
                                    <Tab id="template-workspace-tab-preview" aria-controls="template-workspace-panel-preview" value="preview" label="Preview" />
                                </Tabs>
                            )}
                            {preview && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AssistantIcon />}
                                    onClick={() => setAiDialogOpen(true)}
                                    sx={{ alignSelf: 'flex-end', flex: '0 0 auto', mb: 1 }}
                                >
                                    Open Synergist AI
                                </Button>
                            )}
                            <Box
                                ref={templateWorkspaceRef}
                                data-testid="template-workspace-layout"
                                data-layout="content-only"
                                sx={{
                                    height: '100%',
                                    minHeight: 0,
                                    overflow: 'hidden'
                                }}
                            >
                                <Stack
                                    spacing={1.5}
                                    data-testid="template-primary-workspace"
                                    id={singlePane ? `template-workspace-panel-${workspaceSection}` : undefined}
                                    role={singlePane ? 'tabpanel' : undefined}
                                    aria-labelledby={singlePane ? `template-workspace-tab-${workspaceSection}` : undefined}
                                    sx={{
                                        minWidth: 0,
                                        minHeight: 0,
                                        height: '100%',
                                        overflowY: singlePane && workspaceSection === 'details' ? 'auto' : 'hidden',
                                    }}
                                >
                                    {(!singlePane || workspaceSection === 'details') && (<>
                                    <Box
                                        data-testid="template-fields"
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: singlePane ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(190px, 1fr))',
                                            gap: theme.spacing(1.5),
                                            paddingTop: theme.spacing(1),
                                            flex: '0 0 auto'
                                        }}
                                    >
                                        <PositionAutocompleteControl label="Position" positionId={positionId} onUserSelect={handlePositionSelect} />
                                        <TextField
                                            fullWidth
                                            label={I18N.Translate('Template Name')}
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            error={IsEmpty(templateName)}
                                            size="small"
                                        />
                                        <Stack data-testid="process-conversation-row" direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="process-type-label">Process Type</InputLabel>
                                                <Select
                                                    label="Process Type"
                                                    labelId="process-type-label"
                                                    value={processType}
                                                    onChange={(e) => setProcessType(e.target.value as ProcessType)}
                                                >
                                                    <MenuItem value={ProcessType.Evaluate}>Evaluate</MenuItem>
                                                    <MenuItem value={ProcessType.Interview}>Interview</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="contained"
                                                disabled={disableGenerate || aiBusy}
                                                onClick={() => setAiDialogOpen(true)}
                                                sx={{ whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                                            >
                                                Start template conversation
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ flex: '0 0 auto' }}>
                                        <Button
                                            size="small"
                                            color="inherit"
                                            fullWidth
                                            data-testid="job-description-accordion"
                                            aria-expanded={showJobDescription}
                                            aria-controls="template-job-description"
                                            endIcon={showJobDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            onClick={() => setShowJobDescription(current => !current)}
                                            sx={{
                                                justifyContent: 'space-between',
                                                borderTop: 1,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                borderRadius: 0,
                                                py: 1,
                                            }}
                                        >
                                            {showJobDescription ? 'Hide Job Description' : 'Show Job Description'}
                                        </Button>
                                        <Collapse in={showJobDescription}>
                                            <Paper
                                                id="template-job-description"
                                                role="region"
                                                aria-label="Job Description"
                                                variant="outlined"
                                                sx={{
                                                    marginTop: 1,
                                                    padding: 1.5,
                                                    maxHeight: singlePane ? 'none' : 150,
                                                    overflowY: singlePane ? 'visible' : 'auto',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {jobDescription || 'No job description is available for this position.'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Job descriptions are managed on the position.
                                                </Typography>
                                            </Paper>
                                        </Collapse>
                                    </Box>

                                    <Stack
                                        direction={viewport.isPhone ? 'column' : 'row'}
                                        spacing={1}
                                        sx={{ flex: '0 0 auto', alignItems: viewport.isPhone ? 'stretch' : 'center' }}
                                    >
                                        {preview && (
                                            <Button
                                                variant="text"
                                                disabled={disableGenerate || aiBusy}
                                                onClick={() => handleGeneratePreview(true)}
                                            >
                                                Regenerate
                                            </Button>
                                        )}
                                    </Stack>
                                    </>)}

                                    {(!singlePane || workspaceSection === 'preview') && (
                                    <Paper
                                        variant="outlined"
                                        data-testid="template-preview-scroll"
                                        sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: theme.spacing(2) }}
                                    >
                                        {!preview ? (
                                            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100%' }}>
                                                <Typography color="text.secondary" align="center">
                                                    Select a position and generate a preview to begin refining the template.
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                    <Typography variant="subtitle1">Template Preview</Typography>
                                                    {previewDiff && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Changes: +{previewDiff.added.length} / -{previewDiff.removed.length}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {preview.ProficiencyGroups.map((group) => {
                                                    if (group.Proficiencies.length === 0)
                                                        return null;

                                                    return (
                                                        <Box key={group.ProficiencyGroup}>
                                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                                <Typography variant="subtitle2">{group.ProficiencyGroup}</Typography>
                                                                <Chip size="small" variant="outlined" label={`${getWeightSelectionLabel(group.Weight)} group weight`} />
                                                            </Stack>
                                                            <Stack spacing={1} sx={{ marginTop: theme.spacing(1) }}>
                                                                {group.Proficiencies.map((item, index) => {
                                                                    const key = getSuggestionKey(group.ProficiencyGroup, item);
                                                                    const isDenied = suggestionDecisions[key]?.Decision === 'denied';

                                                                    return (
                                                                        <Paper
                                                                            key={`${key}-${index}`}
                                                                            variant="outlined"
                                                                            data-denied={isDenied}
                                                                            sx={{
                                                                                padding: 1.25,
                                                                                opacity: isDenied ? 0.48 : 1,
                                                                                backgroundColor: isDenied ? 'action.disabledBackground' : 'background.paper',
                                                                                transition: 'opacity 120ms ease, background-color 120ms ease'
                                                                            }}
                                                                        >
                                                                            <Stack spacing={1}>
                                                                                <Typography variant="body2">{item.Description}</Typography>
                                                                                <Stack
                                                                                    direction="row"
                                                                                    alignItems="center"
                                                                                    flexWrap="wrap"
                                                                                    sx={{ gap: 0.75 }}
                                                                                >
                                                                                    <Chip
                                                                                        size="small"
                                                                                        color={item.ReusesExistingProficiency ? 'success' : 'info'}
                                                                                        variant={item.ReusesExistingProficiency ? 'filled' : 'outlined'}
                                                                                        label={item.ReusesExistingProficiency ? 'Existing library match' : 'Suggested addition'}
                                                                                    />
                                                                                    <Chip size="small" variant="outlined" label={item.Type} />
                                                                                    <Chip size="small" variant="outlined" label={getWeightSelectionLabel(item.Weight)} />
                                                                                    {item.IsNegative && <Chip size="small" color="warning" variant="outlined" label="Negative behavior" />}
                                                                                    <Box sx={{ marginLeft: { sm: 'auto' } }}>
                                                                                        <Button
                                                                                            size="small"
                                                                                            color={isDenied ? 'primary' : 'inherit'}
                                                                                            aria-label={`${isDenied ? 'Undo denial for' : 'Deny'} ${item.Description}`}
                                                                                            onClick={() => handleSuggestionDenialToggle(group.ProficiencyGroup, item, !isDenied)}
                                                                                        >
                                                                                            {isDenied ? 'Undo denial' : 'Deny'}
                                                                                        </Button>
                                                                                    </Box>
                                                                                </Stack>
                                                                            </Stack>
                                                                        </Paper>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        )}
                                    </Paper>
                                    )}
                                </Stack>

                                <ContextualAiDialog
                                    open={aiDialogOpen}
                                    onClose={() => {
                                        setAiDialogOpen(false);
                                    }}
                                    anchorEl={templateWorkspaceRef.current}
                                    title="Synergist AI — Template Generation"
                                    workflow="template-generation"
                                >
                                    {renderTemplateConversation()}
                                </ContextualAiDialog>
                            </Box>
                        </DialogContent>
                        <StickyDialogActions
                            phoneLayout="stacked"
                            sx={{
                                flex: '0 0 auto',
                                margin: 0,
                                padding: theme.spacing(1),
                                borderTop: singlePane ? 1 : 0,
                            }}
                        >
                            <Button onClick={handleCancel} variant='text' color='inherit'>Cancel</Button>
                            <Button disabled={!preview} onClick={handleReject} variant='text' color='error'>Reject AI Result</Button>
                            <Button disabled={disableCreate} onClick={handleApprove} variant='outlined' color='primary'>
                                {!hasConfirmedCoverage ? 'Confirm category coverage' : 'Create Template'}
                            </Button>
                        </StickyDialogActions>
                    </Box>
                </BusySpinner>
            </ResponsiveDialog>
        </>
    );
};

const normalizeConversationText = (value: string) => value.trim().replace(/\s+/g, ' ');

const appendAssistantResponse = (messages: AiChatMessage[], assistantMessage: string): AiChatMessage[] => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant'
        && normalizeConversationText(lastMessage.content) === normalizeConversationText(assistantMessage))
        return messages;

    return [
        ...messages,
        { role: 'assistant', content: assistantMessage },
    ];
};

const computePreviewDiff = (previous: AiTemplatePreview | null, current: AiTemplatePreview | null) => {
    if (!previous || !current)
        return null;

    const prevSet = flattenPreview(previous);
    const currSet = flattenPreview(current);

    const added = [...currSet].filter(x => !prevSet.has(x));
    const removed = [...prevSet].filter(x => !currSet.has(x));

    return { added, removed };
};

const flattenPreview = (preview: AiTemplatePreview) => {
    const items = preview.ProficiencyGroups.flatMap(group =>
        group.Proficiencies.map(item =>
            `${group.ProficiencyGroup}|${item.Type}|${item.IsNegative ? 'neg' : 'pos'}|${item.Description}`.toLowerCase()
        )
    );
    return new Set(items);
};

const getSuggestionKey = (groupName: string, item: AiTemplateItem) => [
    groupName,
    item.ExistingProficiencyId ?? '',
    item.Type,
    item.IsNegative ? 'negative' : 'positive',
    item.Description
].join('|').toLowerCase();

const buildIncludedPreview = (
    preview: AiTemplatePreview,
    decisions: Record<string, AiTemplateSuggestionDecision>
): AiTemplatePreview => ({
    ...preview,
    ProficiencyGroups: preview.ProficiencyGroups
        .map(group => ({
            ...group,
            Proficiencies: group.Proficiencies.filter(item =>
                decisions[getSuggestionKey(group.ProficiencyGroup, item)]?.Decision !== 'denied'
            )
        }))
});

const requiredProficiencyGroups = ['Sustainable', 'Performant', 'Adaptable', 'Communication', 'Culture'];

const mapServerCoverageDecision = (decision: AiContextualCoverageDecisionWire) => ({
    category: decision.ProficiencyGroup,
    includedCount: decision.IncludedCount,
    heuristicCount: decision.RecommendedMinimum,
    coverageFingerprint: decision.CoverageFingerprint,
    coverageContractVersion: decision.CoverageContractVersion,
    availableActions: decision.AvailableActions.filter((action): action is 'AddSuggestedItems' | 'DescribeMissingCoverage' | 'RegenerateCategory' | 'ConfirmSufficient' =>
        ['AddSuggestedItems', 'DescribeMissingCoverage', 'RegenerateCategory', 'ConfirmSufficient'].includes(action)),
    coverageRationale: decision.QuestionText,
    suggestions: decision.SuggestedAdditions.map((item, index) => ({
        id: item.ExistingProficiencyId ?? `${decision.ProficiencyGroup}-suggestion-${index}`,
        label: item.Description,
    })),
});

const coverageVersionFor = (decisions: AiContextualCoverageDecisionWire[]) => {
    const versions = [...new Set(decisions.map(decision => decision.CoverageContractVersion))];
    return versions.length === 1 ? versions[0] : undefined;
};

const serverCoverageIsConfirmed = (
    decisions: AiContextualCoverageDecisionWire[],
    confirmations: AiTemplateCoverageConfirmation[],
) => decisions.length > 0 && decisions.every(decision => confirmations.some(confirmation =>
    confirmation.ProficiencyGroup === decision.ProficiencyGroup
    && confirmation.CoverageFingerprint === decision.CoverageFingerprint
    && confirmation.ConfirmedSufficient
    && Boolean(confirmation.ContextSummary?.trim())
));

const findServerCoverageDecision = (
    decisions: AiContextualCoverageDecisionWire[],
    confirmations: AiTemplateCoverageConfirmation[],
    changedGroups: string[],
) => {
    const next = decisions.find(decision => {
        const confirmed = confirmations.some(confirmation => confirmation.ProficiencyGroup === decision.ProficiencyGroup
            && confirmation.CoverageFingerprint === decision.CoverageFingerprint
            && confirmation.ConfirmedSufficient);
        return !confirmed && (changedGroups.includes(decision.ProficiencyGroup) || decision.IncludedCount < decision.RecommendedMinimum);
    });
    return next ? mapServerCoverageDecision(next) : null;
};

const buildServerCoverageSummary = (decisions: AiContextualCoverageDecisionWire[]) => ({
    contextSummary: 'Together these categories balance role outcomes, essential capabilities, positive practices, collaboration, and a limited set of meaningful risks.',
    categories: requiredProficiencyGroups
        .map(groupName => decisions.find(decision => decision.ProficiencyGroup === groupName))
        .filter((decision): decision is AiContextualCoverageDecisionWire => Boolean(decision))
        .map(mapServerCoverageDecision),
});

const discoveryTopicGap: Record<string, AiContextualAiAnswer['GapKey']> = {
    outcomes: 'RoleOutcomesAndFailureCosts',
    abilities: 'TechnicalDomainAbilities',
    autonomy: 'DecisionAuthority',
    'risks-and-prevention': 'HighImpactMistakes',
    'positive-practices': 'PositiveBehaviors',
    collaboration: 'CollaborationAudiences',
    'safety-compliance-ethics': 'SafetyComplianceEthics',
    'performance-contrast': 'PerformanceDistinction',
};

const buildSuggestionDecisionPayload = (
    decisions: Record<string, AiTemplateSuggestionDecision>
) => Object.values(decisions);

const weightSelectionLabels: Record<number, string> = {
    1: 'Least Important',
    2: 'Standard Less Three',
    3: 'Standard Less Two',
    4: 'Standard Less One',
    5: 'Standard',
    6: 'Standard Plus One',
    7: 'Standard Plus Two',
    8: 'Standard Plus Three',
    9: 'Standard Plus Four',
    10: 'Most Important'
};

const getWeightSelectionLabel = (weight: number) => weightSelectionLabels[weight] ?? `Weight ${weight}`;

export default TemplateAiUploadPopup;
